import { invalidateLiveTranscript } from '@/hooks/query/useQueryLiveTranscriptBySessionId';
import {
  completeRecordingFinalizeAttempt,
  type RecordingFinalizePipeline,
} from '@/lib/telemetry/recordingFinalizeTracking';
import { isOnPrem } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { type LedgerRecordingCompletion } from '@heidi/capture/ports';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { finishChunkUploadV3 } from './chunkUploadV3';

type FinishRequest = {
  recordingId: string;
  pipeline: RecordingFinalizePipeline;
  gaps: LedgerRecordingCompletion['gaps'];
  sessionDeleted: boolean;
};

/**
 * What `:finish` reported. The legacy HTTP handler resolves a dead-session 401
 * or a suspended-account 403 with no response, after a logout or a redirect:
 * nothing was finalized then (`unanswered`). A 200 still carries
 * `is_success: false` when the server could not aggregate the chunks.
 */
type FinishAnswer = 'finalized' | 'not_finalized' | 'unanswered';

// The interceptor hands back the axios response, so the body sits under `data`.
const readFinishAnswer = (response: unknown): FinishAnswer => {
  if (response == null) return 'unanswered';
  const body = (response as { data?: { is_success?: unknown } }).data;
  return body?.is_success === false ? 'not_finalized' : 'finalized';
};

export const useFinishChunkRecording = (sessionId: string) => {
  const { mutate } = useMutation<FinishAnswer, unknown, FinishRequest>({
    mutationFn: async ({ recordingId }) => {
      const response: unknown = await scribeHttpV2.post<{
        is_success: boolean;
        recording_id: string;
      }>(
        `/sessions/${sessionId}/restful-segment-transcription/${recordingId}:finish`,
        {}
      );
      return readFinishAnswer(response);
    },
    onSuccess: (answer, { recordingId, pipeline, gaps, sessionDeleted }) => {
      completeRecordingFinalizeAttempt(recordingId, {
        via: 'finish_request',
        pipeline,
        gaps,
        sessionDeleted,
        interrupted: answer === 'unanswered',
        finishFailed: answer === 'not_finalized',
      });
      // On-prem: :finish stamps stopped_at and aggregates fragments. Refresh
      // with status=complete once the chunk pipeline is drained.
      if (isOnPrem) {
        void invalidateLiveTranscript(sessionId);
      }
    },
    onError: (error, { recordingId, pipeline, gaps, sessionDeleted }) => {
      completeRecordingFinalizeAttempt(recordingId, {
        via: 'finish_request',
        pipeline,
        gaps,
        sessionDeleted,
        error,
      });
    },
  });

  return useCallback(
    (
      recordingId: string,
      useTranscriptionServiceV2: boolean,
      ledgerCompletion?: LedgerRecordingCompletion
    ) => {
      // The legacy pipeline reports no ledger facts, so its finalize opens here.
      const pipeline: RecordingFinalizePipeline = ledgerCompletion ? 'ledger' : 'legacy';
      const gaps = ledgerCompletion?.gaps ?? { failed: 0, abandoned: 0 };
      const sessionDeleted = ledgerCompletion?.sessionDeleted ?? false;
      // v3 recordings finish via their stop marker, not an API call.
      if (ledgerCompletion?.uploadTransport?.kind === 'temporal') {
        void finishChunkUploadV3(
          ledgerCompletion.sessionId,
          recordingId,
          ledgerCompletion.uploadTransport,
          ledgerCompletion.lastChunkIndex,
          ledgerCompletion.gaps
        ).then(
          (marker) =>
            completeRecordingFinalizeAttempt(recordingId, {
              via: 'stop_marker',
              pipeline,
              gaps,
              sessionDeleted,
              finishFailed: marker === 'failed',
            }),
          (error: unknown) => {
            // The class only: the metric alone cannot say why the marker threw.
            logger.warn('recording_finalize_stop_marker_threw', {
              errorName: error instanceof Error ? error.name : typeof error,
            });
            completeRecordingFinalizeAttempt(recordingId, {
              via: 'stop_marker',
              pipeline,
              gaps,
              sessionDeleted,
              finishFailed: true,
            });
          }
        );
        return;
      }
      // Exhaustiveness: a future transport kind must not silently take the
      // legacy path.
      ledgerCompletion?.uploadTransport satisfies { kind: 'http' } | undefined;
      if (!useTranscriptionServiceV2) {
        mutate({ recordingId, pipeline, gaps, sessionDeleted });
        return;
      }
      // v2 finalizes server-side once every chunk lands, so the delivery itself
      // is the finalize.
      completeRecordingFinalizeAttempt(recordingId, {
        via: 'server_auto',
        pipeline,
        gaps,
        sessionDeleted,
      });
    },
    [mutate]
  );
};
